import { currentBoxSubdomain } from '@src/routing/boxDomain';
import { parseGamePath } from '@src/routing/routes';
import { swallowAccessTokenFromUrl } from '@src/access/swallowAccessToken';

export const NOTIFICATION_ACCESS_TOKEN_KEY = 'santa_notification_access_token';
export const NOTIFICATION_ACCESS_REASON_KEY = 'santa_notification_access_reason';
export const NOTIFICATION_ACCESS_EMAIL_KEY = 'santa_notification_access_email';

export type NotificationAccessRecoverReason = 'expired' | 'consumed' | 'superseded' | 'unavailable';

export function swallowNotificationAccessTokenFromUrl(): void {
  // Accept already-sent experimental links through the standard email-entry flow.
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    const label = currentBoxSubdomain();
    const notification = url.searchParams.get('notification');
    if (label && notification && parseGamePath(url.pathname, label)) {
      url.pathname = '/open';
      url.search = '';
      url.searchParams.set('token', notification);
      window.history.replaceState(window.history.state, '', url.pathname + url.search);
    }
  }
  if (typeof window !== 'undefined' && window.location.pathname.replace(/\/$/, '') === '/open'
    && new URLSearchParams(window.location.search).has('token')) {
    sessionStorage.removeItem(NOTIFICATION_ACCESS_EMAIL_KEY);
    sessionStorage.removeItem(NOTIFICATION_ACCESS_REASON_KEY);
  }
  swallowAccessTokenFromUrl({
    matchesPath: (pathname) => pathname.replace(/\/$/, '') === '/open',
    storageKey: () => NOTIFICATION_ACCESS_TOKEN_KEY,
    keepOtherSearchParams: false,
  });
}

export function getNotificationAccessToken(): string | null {
  swallowNotificationAccessTokenFromUrl();
  if (typeof window === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(NOTIFICATION_ACCESS_TOKEN_KEY);
}

export function clearNotificationAccessToken(): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(NOTIFICATION_ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(NOTIFICATION_ACCESS_REASON_KEY);
  sessionStorage.removeItem(NOTIFICATION_ACCESS_EMAIL_KEY);
}

export function setNotificationAccessEmail(emailMasked: string): void {
  if (!emailMasked) {
    sessionStorage.removeItem(NOTIFICATION_ACCESS_EMAIL_KEY);
    return;
  }
  sessionStorage.setItem(NOTIFICATION_ACCESS_EMAIL_KEY, emailMasked);
}

export function getNotificationAccessEmail(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return sessionStorage.getItem(NOTIFICATION_ACCESS_EMAIL_KEY) || '';
}

export function setNotificationAccessReason(reason: NotificationAccessRecoverReason): void {
  sessionStorage.setItem(NOTIFICATION_ACCESS_REASON_KEY, reason);
}

export function getNotificationAccessReason(): NotificationAccessRecoverReason | null {
  const reason = sessionStorage.getItem(NOTIFICATION_ACCESS_REASON_KEY);
  if (
    reason === 'expired' ||
    reason === 'consumed' ||
    reason === 'superseded' ||
    reason === 'unavailable'
  ) {
    return reason;
  }
  return null;
}

export function recoverReasonFromError(
  message?: string,
): NotificationAccessRecoverReason | 'malformed' {
  switch (message) {
    case 'NOTIFICATION_ACCESS_EXPIRED':
      return 'expired';
    case 'NOTIFICATION_ACCESS_CONSUMED':
      return 'consumed';
    case 'NOTIFICATION_ACCESS_SUPERSEDED':
      return 'superseded';
    case 'NOTIFICATION_ACCESS_UNAVAILABLE':
      return 'unavailable';
    default:
      return 'malformed';
  }
}
